import { apiService } from '@/shared/services/api';
import { endpoints } from '@/shared/services/endpoints';
import type { ApiResponse } from '@/shared/types/api';
import type {
  CampaignPayment,
  LaunchOutcome,
  MetaPlacement,
  PaymentAuthorization,
} from '../types/checkout.types';

/** Hold the price on the card; returns the client secret to confirm. */
export async function authorizeCampaignPayment(
  campaignId: string,
  placements: MetaPlacement[],
): Promise<PaymentAuthorization> {
  const { data } = await apiService.post<ApiResponse<PaymentAuthorization>>(
    endpoints.campaigns.paymentIntent(campaignId),
    { placements },
  );
  return data.data;
}

/** Latest payment for a campaign; null when none exists yet. */
export async function fetchCampaignPayment(campaignId: string): Promise<CampaignPayment | null> {
  try {
    const { data } = await apiService.get<ApiResponse<CampaignPayment>>(
      endpoints.campaigns.payment(campaignId),
    );
    return data.data;
  } catch (error) {
    // The api interceptor rejects with a plain ApiError object.
    if (typeof error === 'object' && error !== null && 'status' in error && error.status === 404) {
      return null;
    }
    throw error;
  }
}

export async function captureCampaignPayment(
  campaignId: string,
  outcome: LaunchOutcome,
): Promise<CampaignPayment> {
  const { data } = await apiService.post<ApiResponse<CampaignPayment>>(
    endpoints.campaigns.paymentCapture(campaignId),
    { outcome },
  );
  return data.data;
}

export async function cancelCampaignPayment(campaignId: string): Promise<CampaignPayment> {
  const { data } = await apiService.post<ApiResponse<CampaignPayment>>(
    endpoints.campaigns.paymentCancel(campaignId),
  );
  return data.data;
}
